/**
 * The app bar's single content slot.
 *
 * The bar (`app/components/shell/AppBar.vue`) spans the whole app above the rail and the columns, and the
 * middle of it belongs to whatever page is open: its breadcrumbs and its actions. Rather than teach
 * the bar about routes, the page pushes into it — `PageContent`, the docs viewer and the sheet
 * toolbar all render their header row into `#marcus-appbar-slot` via `<Teleport>`.
 *
 * Two claimants can be alive at once, and they need opposite answers:
 *
 * **Nested — the deepest wins.** `admin.vue` wraps `<NuxtPage/>` in a `PageContent`, and its child
 * route renders one too. The child's trail already contains the parent's (`Admin › Tipos de…`), so
 * the specific header supersedes the general one. Mount order cannot express this: `onMounted` fires
 * child-first, so the OUTER claimant would always land last. Hence an explicit depth, provided down
 * the tree.
 *
 * **Sibling — the newest wins.** During a route transition the incoming page mounts before the
 * outgoing one unmounts. At equal depth the later claim owns the bar, so the trail never flickers
 * empty on navigation, and a claimant leaving early falls back to the one still mounted.
 *
 * A claimant renders its teleport only while `active` is true, so exactly one header exists in the
 * DOM at a time and nothing is left stranded in the content column.
 */

/** The teleport target's element id. Rendered by `AppBar.vue`; there is only ever one. */
export const APP_BAR_SLOT_ID = 'marcus-appbar-slot'

/** Nesting depth of the enclosing claimant, if any. */
const APP_BAR_DEPTH = Symbol('marcus-appbar-depth') as InjectionKey<number>

export interface AppBarClaim {
  id: number
  depth: number
}

/**
 * Which of the live claims owns the bar: the deepest, and among equals the most recently pushed.
 * Exported and pure so the rule is testable without a Nuxt app — `tests/unit/appBarSlot.test.ts`.
 */
export function pickAppBarClaim(claims: AppBarClaim[]): AppBarClaim | undefined {
  let winner: AppBarClaim | undefined
  for (const claim of claims) {
    if (!winner || claim.depth >= winner.depth) winner = claim
  }
  return winner
}

/**
 * Module-scoped and monotonic: the value only has to be unique among claimants alive at the same
 * moment, so it never needs resetting between requests.
 */
let seq = 0

/**
 * True while some page owns the bar's slot. `AppBar.vue` reads it to show its phone-only fallback
 * title (`utils/mobileHeaderTitle.ts`) only when no page has supplied one of its own. It is derived
 * from the same claim stack as `useAppBarSlot`, so there is no second source of truth to keep in step.
 */
export function useAppBarClaimed() {
  const stack = useState<AppBarClaim[]>('appbar-slot-stack', () => [])
  return computed(() => stack.value.length > 0)
}

export function useAppBarSlot() {
  const stack = useState<AppBarClaim[]>('appbar-slot-stack', () => [])
  const id = ++seq
  const depth = inject(APP_BAR_DEPTH, 0) + 1

  // Anything rendered inside this claimant (a child route in a slot) claims one level deeper.
  provide(APP_BAR_DEPTH, depth)

  const active = computed(() => pickAppBarClaim(stack.value)?.id === id)

  onMounted(() => {
    stack.value = [...stack.value, { id, depth }]
  })

  onUnmounted(() => {
    stack.value = stack.value.filter(claim => claim.id !== id)
  })

  return {
    /** True while this claimant owns the bar. Gate the `<Teleport>` on it. */
    active,
    /** Selector for `<Teleport :to>`. */
    target: `#${APP_BAR_SLOT_ID}`,
  }
}
